export type SupportedBrowser = "chrome" | "edge" | "firefox" | "safari" | "unknown";

export interface BrowserCompatibility {
  browser: SupportedBrowser;
  version: number;
  supported: boolean;
  message: string;
}

interface BrowserCompatibilityOptions {
  userAgent?: string;
  hasGetUserMedia?: boolean;
  hasPeerConnection?: boolean;
  hasRtpTransceiver?: boolean;
}

const minimumVersions: Record<Exclude<SupportedBrowser, "unknown">, number> = {
  chrome: 74,
  edge: 88,
  firefox: 78,
  safari: 12,
};

function detectBrowser(userAgent: string): { browser: SupportedBrowser; version: number } {
  const edge = /Edg\/(\d+)/i.exec(userAgent);
  if (edge) return { browser: "edge", version: Number(edge[1]) };

  const firefox = /Firefox\/(\d+)/i.exec(userAgent);
  if (firefox) return { browser: "firefox", version: Number(firefox[1]) };

  const chrome = /(?:Chrome|Chromium)\/(\d+)/i.exec(userAgent);
  if (chrome) return { browser: "chrome", version: Number(chrome[1]) };

  const safari = /Version\/(\d+)(?:\.\d+)*.*Safari\//i.exec(userAgent);
  if (safari) return { browser: "safari", version: Number(safari[1]) };

  return { browser: "unknown", version: 0 };
}

export function getBrowserCompatibility(
  options: BrowserCompatibilityOptions = {}
): BrowserCompatibility {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const userAgent = options.userAgent ?? nav?.userAgent ?? "";
  const { browser, version } = detectBrowser(userAgent);
  const hasGetUserMedia =
    options.hasGetUserMedia ?? typeof nav?.mediaDevices?.getUserMedia === "function";
  const hasPeerConnection =
    options.hasPeerConnection ?? typeof RTCPeerConnection !== "undefined";
  const hasRtpTransceiver =
    options.hasRtpTransceiver ?? typeof RTCRtpTransceiver !== "undefined";

  if (!hasGetUserMedia || !hasPeerConnection || !hasRtpTransceiver) {
    return {
      browser,
      version,
      supported: false,
      message: "当前浏览器缺少 WebRTC 能力，无法进入会议，请升级浏览器后重试",
    };
  }

  if (browser === "unknown") {
    return {
      browser,
      version,
      supported: false,
      message: "当前浏览器尚未完成兼容验证，建议使用最新版 Chrome、Edge、Firefox 或 Safari",
    };
  }

  const minimumVersion = minimumVersions[browser];
  const supported = version >= minimumVersion;

  return {
    browser,
    version,
    supported,
    message: supported
      ? ""
      : `当前浏览器版本过低，请升级到 ${browser} ${minimumVersion} 或更高版本`,
  };
}
